import { Component } from '@theme/component';
import { entryFromItem, rankEntries, searchTerms } from '@theme/faq-search';

const MIN_QUERY_LENGTH = 3;
const MAX_SUGGESTIONS = 5;

/**
 * Kundenservice hero search: an ARIA 1.2 combobox that suggests matching
 * questions from the page's customer-faq section while typing. Picking one
 * opens exactly that answer; submitting filters the FAQ with the query.
 * Without an FAQ on the page the form falls back to /search.
 *
 * @typedef {{ id: string, question: string, topic: string, entry: import('@theme/faq-search').SearchEntry }} Suggestion
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} input
 * @property {HTMLUListElement} listbox
 * @property {HTMLElement} status
 *
 * @extends {Component<Refs>}
 */
class KsHeroSearch extends Component {
  requiredRefs = ['input', 'listbox', 'status'];

  /** @type {Suggestion[] | null} */
  #index = null;
  /** @type {HTMLLIElement[]} */
  #options = [];
  #activeIndex = -1;

  connectedCallback() {
    super.connectedCallback();
    // Keep focus in the input while clicking an option, so blur doesn't close the list first.
    this.refs.listbox.addEventListener('mousedown', this.#keepFocus);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.listbox.removeEventListener('mousedown', this.#keepFocus);
  }

  suggest = () => {
    const query = this.refs.input.value;
    const terms = query.trim().length >= MIN_QUERY_LENGTH ? searchTerms(query) : [];
    const index = this.#getIndex();
    if (terms.length === 0 || index.length === 0) {
      this.close();
      return;
    }

    const ranked = rankEntries(
      index.map((suggestion) => suggestion.entry),
      terms
    )
      .map((result, i) => ({ ...result, suggestion: index[i] }))
      .filter((result) => result.matches)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_SUGGESTIONS)
      .map((result) => result.suggestion);

    this.#render(ranked);
  };

  /** @param {KeyboardEvent} event */
  navigate = (event) => {
    const { listbox } = this.refs;

    if (listbox.hidden) {
      if (event.key === 'ArrowDown' && this.refs.input.value) {
        event.preventDefault();
        this.suggest();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        if (this.#options.length === 0) return;
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = (this.#activeIndex + step + this.#options.length) % this.#options.length;
        this.#setActive(next);
        break;
      }
      case 'Enter':
        if (this.#activeIndex >= 0) {
          event.preventDefault();
          this.#choose(this.#options[this.#activeIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
    }
  };

  /** @param {Event} event */
  selectOption = (event) => {
    const option = event.target;
    if (option instanceof HTMLLIElement) this.#choose(option);
  };

  close = () => {
    const { input, listbox, status } = this.refs;
    listbox.hidden = true;
    listbox.replaceChildren();
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    status.textContent = '';
    this.#options = [];
    this.#activeIndex = -1;
  };

  /** @param {SubmitEvent} event */
  submitSearch = (event) => {
    this.close();
    if (this.#getIndex().length === 0) return;

    event.preventDefault();
    const search = this.refs.input.value.trim();
    if (!search) {
      this.refs.input.focus();
      return;
    }

    document.dispatchEvent(new CustomEvent('customer-faq:open', { detail: { search } }));
    window.phCapture?.('kundenservice_faq_shortcut_clicked', { source: 'hero', topic: null, search, label: search });
  };

  /** @param {MouseEvent} event */
  #keepFocus = (event) => {
    event.preventDefault();
  };

  /** @returns {Suggestion[]} */
  #getIndex() {
    if (this.#index === null) {
      const items = /** @type {HTMLElement[]} */ ([...document.querySelectorAll('customer-faq-component [data-faq-id]')]);
      this.#index = items.map((item) => ({
        id: item.dataset.faqId ?? '',
        question: item.querySelector('.customer-faq__item-question')?.textContent?.trim() ?? '',
        topic: item.dataset.topicTitle ?? '',
        entry: entryFromItem(item),
      }));
    }
    return this.#index;
  }

  /** @param {Suggestion[]} suggestions */
  #render(suggestions) {
    const { input, listbox, status } = this.refs;
    const { countText = '', emptyText = '', emptyLink = '', emptyLinkLabel = '' } = this.dataset;

    this.#options = suggestions.map((suggestion, i) => {
      const option = document.createElement('li');
      option.className = 'ks-hero__suggestion';
      option.id = `${listbox.id}-option-${i}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.setAttribute('on:click', '/selectOption');
      option.dataset.faqId = suggestion.id;

      const question = document.createElement('span');
      question.className = 'ks-hero__suggestion-question';
      question.textContent = suggestion.question;
      option.append(question);

      if (suggestion.topic) {
        const topic = document.createElement('span');
        topic.className = 'ks-hero__suggestion-topic';
        topic.textContent = suggestion.topic;
        option.append(topic);
      }
      return option;
    });

    if (this.#options.length === 0) {
      const option = document.createElement('li');
      option.className = 'ks-hero__suggestion';
      option.id = `${listbox.id}-option-empty`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.setAttribute('on:click', '/selectOption');

      const text = document.createElement('span');
      text.className = 'ks-hero__suggestion-empty';
      text.textContent = emptyText;
      option.append(text);

      if (emptyLink && emptyLinkLabel) {
        option.dataset.href = emptyLink;
        const link = document.createElement('span');
        link.className = 'ks-hero__suggestion-link';
        link.textContent = emptyLinkLabel;
        option.append(link);
      }
      this.#options = [option];
    }

    listbox.replaceChildren(...this.#options);
    listbox.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    input.removeAttribute('aria-activedescendant');
    this.#activeIndex = -1;
    status.textContent = suggestions.length > 0 ? countText.replace('{count}', String(suggestions.length)) : emptyText;
  }

  /** @param {number} index */
  #setActive(index) {
    this.#options.forEach((option, i) => option.setAttribute('aria-selected', i === index ? 'true' : 'false'));
    const active = this.#options[index];
    this.refs.input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
    this.#activeIndex = index;
  }

  /** @param {HTMLLIElement} option */
  #choose(option) {
    const query = this.refs.input.value.trim();
    const { faqId, href } = option.dataset;
    const position = this.#options.indexOf(option) + 1;
    const question = option.querySelector('.ks-hero__suggestion-question')?.textContent ?? '';
    this.close();

    if (faqId) {
      document.dispatchEvent(new CustomEvent('customer-faq:open', { detail: { item: faqId } }));
      window.phCapture?.('kundenservice_hero_suggestion_selected', { query, question, position });
    } else if (href) {
      window.phCapture?.('kundenservice_hero_no_suggestion_contact', { query });
      window.location.assign(href);
    }
  }
}

if (!customElements.get('ks-hero-search')) {
  customElements.define('ks-hero-search', KsHeroSearch);
}
